import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import InvestorLayout from '../../components/InvestorLayout';
import { Search } from 'lucide-react';

export default function InvestmentDetails({ user, onLogout }) {
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchInvestments();
  }, []);

  const fetchInvestments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/investor/investments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvestments(response.data);
    } catch (error) {
      console.error('Failed to fetch investments:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredInvestments = investments.filter(inv =>
    inv.investment_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <InvestorLayout user={user} onLogout={onLogout} currentPage="investments">
      <div className="space-y-6" data-testid="investment-details-page">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            My Investments
          </h1>
          <p className="text-muted-foreground text-lg">View all your investment details</p>
        </div>

        <div className="card">
          <div className="p-6 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                data-testid="search-investments-input"
                type="text"
                placeholder="Search investments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="investments-table">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Investment ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Principal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Current Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Interest Earned
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Interest Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Maturity Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-muted-foreground">
                      Loading...
                    </td>
                  </tr>
                ) : filteredInvestments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-muted-foreground">
                      No investments found
                    </td>
                  </tr>
                ) : (
                  filteredInvestments.map((investment) => (
                    <tr key={investment.investment_id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-6 py-4 font-mono text-sm">{investment.investment_id}</td>
                      <td className="px-6 py-4 font-mono font-semibold">
                        ₹{investment.principal_amount.toLocaleString('en-IN')}
                      </td>
                      <td className="px-6 py-4 font-mono font-semibold text-success">
                        ₹{investment.current_value.toLocaleString('en-IN')}
                      </td>
                      <td className="px-6 py-4 font-mono font-semibold text-accent">
                        ₹{investment.total_interest.toLocaleString('en-IN')}
                      </td>
                      <td className="px-6 py-4 capitalize">{investment.interest_type}</td>
                      <td className="px-6 py-4 font-mono">{investment.interest_rate}%</td>
                      <td className="px-6 py-4 text-sm">
                        {new Date(investment.maturity_date).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          investment.status === 'active' ? 'bg-success/10 text-success' :
                          investment.status === 'matured' ? 'bg-accent/10 text-accent' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {investment.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </InvestorLayout>
  );
}